import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { Icon, LoaderComponent } from 'ht-styleguide';

/* Lib */
import HTGladlyChat from './Gladly.methods';

/* Utils */
import { noop } from '@utils/event';
import { sleep } from '@utils/helpers';

/* hooks */
import useGladlyChatLoaded from './useGladlyChatLoaded';

import { Children } from 'types/base.types';

/* Types */
type ChatWithUsProps = {
  classesContainer?: string | string[];
  classesText?: string | string[];
  handleLoadedCallback?: BaseAnyFunction;
  linkText?: React.ReactElement | string;
  showIcon?: boolean;
  iconName?: string;
  iconClasses?: string;
  children?: Children;
  inline?: boolean;
};

/*
 * Adds chat link in the menus and either initiates a chat download or polls till
 * the current is finished, then opens.
 *
 * The reason for this is that on a slow connection a user "could" initiate a download
 * and click on a chat link fast before the download is ready.
 *
 * */
const ChatLink = ({
  classesContainer = '',
  classesText = '',
  handleLoadedCallback = noop,
  linkText = 'Contact Us',
  showIcon = false,
  iconName = 'chat-v2',
  iconClasses = '',
  inline = false,
  children = null,
}: ChatWithUsProps) => {
  /* Hooks */
  const [requestChat, setRequestChat] = useState(false);
  const [loading, setLoading] = useState(false);
  const { gladlyLoaded: previouslyChatLoaded } = useGladlyChatLoaded(loading);

  /* styles */
  const chatLinkContainer = cn('lineItem_chat', classesContainer);
  const chatLinkText = cn(classesText, 'cursorPointer', {
    'paddingRight-medium': !inline && loading,
  });

  const onHandleGladlyLoaded = () => {
    HTGladlyChat.open();
    handleLoadedCallback?.();
  };

  const handleRequestChat = () => {
    setLoading(true);
    setRequestChat(true);
    window.dispatchEvent(new CustomEvent('loadAndOpenChat'));
  };

  const onHandleClick = previouslyChatLoaded ? onHandleGladlyLoaded : handleRequestChat;

  useEffect(() => {
    if (requestChat && previouslyChatLoaded) {
      (async () => {
        await sleep(500);
        HTGladlyChat.open();
        setLoading(false);
        setRequestChat(false);
        handleLoadedCallback?.();
      })();
    }
  }, [requestChat, previouslyChatLoaded, handleLoadedCallback]);

  return (
    <div key="linkMenuChat" className={cn(chatLinkContainer, 'positionRelative')} onClick={onHandleClick}>
      {showIcon && <Icon name={iconName} className={iconClasses} />}
      {children || <div className={chatLinkText}>{linkText}</div>}
      {loading && (
        <div className="positionAbsolute top50 right50">
          <LoaderComponent />
        </div>
      )}
      <style jsx>{`
        .lineItem_chat {
          display: flex;
          flex-direction: row;
          :hover {
            color: #0992e9;
          }
        }
      `}</style>
    </div>
  );
};

export default ChatLink;

/*
  Gladly chat is a big download and really negatively affects performance scores.
  Lets initiate chat opening when user explicilty wants to open it via navigation.
  IF the use doesn't scroll, they can still initiate a download this way.

   @remark
   We should be going to a chat dedicated page instead entwining the app with tons of chat logic.
 */
